import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';

const DedupFirmsModal = ({ onClose, onComplete }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(null);
  const [selections, setSelections] = useState({}); // groupIndex -> { canonicalId, canonicalName, checked: Set }
  const [mergedGroups, setMergedGroups] = useState(new Set());
  const [dismissedGroups, setDismissedGroups] = useState(new Set());
  const [error, setError] = useState(null);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadDuplicates(); }, []);

  const loadDuplicates = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/firms/duplicates`, { headers });
      if (!res.ok) throw new Error('Failed to load duplicates');
      const data = await res.json();
      setGroups(data.groups || []);
      // Default: select first firm in each group as canonical, all others checked
      const sel = {};
      (data.groups || []).forEach((group, i) => {
        const best = group.reduce((a, b) => (b.employment_count > a.employment_count || b.name.length > a.name.length) ? b : a, group[0]);
        const checked = new Set(group.filter(f => f.id !== best.id).map(f => f.id));
        sel[i] = { canonicalId: best.id, canonicalName: best.name, checked };
      });
      setSelections(sel);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const toggleCheck = (groupIdx, firmId) => {
    setSelections(prev => {
      const s = { ...prev };
      const group = { ...s[groupIdx] };
      const checked = new Set(group.checked);
      if (firmId === group.canonicalId) return prev; // can't uncheck canonical
      if (checked.has(firmId)) checked.delete(firmId);
      else checked.add(firmId);
      group.checked = checked;
      s[groupIdx] = group;
      return s;
    });
  };

  const setCanonical = (groupIdx, firmId, firmName) => {
    setSelections(prev => {
      const s = { ...prev };
      const group = { ...s[groupIdx] };
      const checked = new Set(group.checked);
      checked.delete(firmId); // canonical shouldn't be in checked
      if (group.canonicalId !== firmId) checked.add(group.canonicalId); // old canonical becomes checked
      group.canonicalId = firmId;
      group.canonicalName = firmName;
      group.checked = checked;
      s[groupIdx] = group;
      return s;
    });
  };

  const handleMerge = async (groupIdx) => {
    const sel = selections[groupIdx];
    if (!sel || sel.checked.size === 0) return;
    
    setMerging(groupIdx);
    try {
      const res = await fetch(`${API_URL}/firms/merge`, {
        method: 'POST', headers,
        body: JSON.stringify({
          canonical_id: sel.canonicalId,
          duplicate_ids: [...sel.checked],
          canonical_name: sel.canonicalName
        })
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Merge failed'); }
      setMergedGroups(prev => new Set([...prev, groupIdx]));
    } catch (err) { alert('Merge failed: ' + err.message); }
    finally { setMerging(null); }
  };

  const handleMergeAll = async () => {
    for (let i = 0; i < groups.length; i++) {
      if (mergedGroups.has(i) || dismissedGroups.has(i)) continue;
      const sel = selections[i];
      if (sel && sel.checked.size > 0) await handleMerge(i);
    }
    onComplete?.();
  };

  const handleDismiss = async (groupIdx) => {
    const group = groups[groupIdx];
    const firmIds = group.map(f => f.id);
    try {
      const res = await fetch(`${API_URL}/firms/not-duplicates`, {
        method: 'POST', headers,
        body: JSON.stringify({ firm_ids: firmIds })
      });
      if (!res.ok) throw new Error('Dismiss failed');
      setDismissedGroups(prev => new Set([...prev, groupIdx]));
    } catch (err) { alert('Failed to dismiss: ' + err.message); }
  };

  const pendingCount = groups.filter((_, i) => !mergedGroups.has(i) && !dismissedGroups.has(i) && selections[i]?.checked.size > 0).length;

  return (
    <div className="flex-shrink-0 w-[440px] bg-white border-l border-gray-200 flex flex-col animate-[slidein_180ms_ease-out] overflow-hidden">
      <style>{`@keyframes slidein { from { opacity: 0; transform: translateX(20px);} to { opacity: 1; transform: translateX(0);} }`}</style>

        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900">Deduplicate Firms</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {loading ? 'Scanning...' : `${groups.length} potential duplicate group${groups.length !== 1 ? 's' : ''} found`}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
            </div>
          )}

          {error && <div className="text-sm text-red-600 text-center py-8">{error}</div>}

          {!loading && groups.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-12 h-12 text-green-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <p className="text-sm text-gray-600 font-medium">No duplicates found</p>
              <p className="text-xs text-gray-400 mt-1">Your firm names look clean</p>
            </div>
          )}

          {groups.map((group, groupIdx) => {
            const sel = selections[groupIdx];
            const isMerged = mergedGroups.has(groupIdx);
            const isDismissed = dismissedGroups.has(groupIdx);
            if (isDismissed) return null;
            
            return (
              <div key={groupIdx} className={`border rounded-lg overflow-hidden ${isMerged ? 'opacity-50' : ''}`}>
                {/* Group header */}
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">{group.length} similar firms</span>
                    {isMerged && <span className="text-[10px] font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded">Merged</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {!isMerged && (
                      <>
                        <button onClick={() => handleDismiss(groupIdx)}
                          className="text-[10px] font-medium px-2.5 py-1 text-gray-500 border border-gray-300 rounded hover:bg-gray-100">
                          Not Duplicates
                        </button>
                        <button onClick={() => handleMerge(groupIdx)} disabled={merging === groupIdx || !sel || sel.checked.size === 0}
                          className="text-[10px] font-medium px-2.5 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                          {merging === groupIdx ? 'Merging...' : `Merge ${sel?.checked.size || 0} → "${sel?.canonicalName}"`}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Firm rows */}
                <div className="divide-y divide-gray-100">
                  {group.map(firm => {
                    const isCanonical = sel?.canonicalId === firm.id;
                    const isChecked = sel?.checked.has(firm.id);
                    
                    return (
                      <div key={firm.id} className={`flex items-center gap-3 px-3 py-2 ${isCanonical ? 'bg-emerald-50/50' : ''}`}>
                        {/* Checkbox */}
                        <input type="checkbox" checked={isCanonical || isChecked} disabled={isCanonical || isMerged}
                          onChange={() => toggleCheck(groupIdx, firm.id)}
                          className="rounded border-gray-300 accent-emerald-600 w-4 h-4 flex-shrink-0" />

                        {/* Firm info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">{firm.name}</span>
                            {isCanonical && <span className="text-[9px] font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded border border-emerald-200">Keep</span>}
                          </div>
                          <span className="text-[10px] text-gray-400">
                            {firm.candidate_count} candidate{firm.candidate_count != 1 ? 's' : ''} · {firm.employment_count} position{firm.employment_count != 1 ? 's' : ''}
                          </span>
                        </div>

                        {/* Set as canonical */}
                        {!isCanonical && !isMerged && (
                          <button onClick={() => setCanonical(groupIdx, firm.id, firm.name)}
                            className="text-[10px] text-emerald-600 hover:text-emerald-800 font-medium whitespace-nowrap">
                            Use this name
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Editable canonical name */}
                {!isMerged && sel && (
                  <div className="px-3 py-2 bg-gray-50 border-t">
                    <label className="text-[10px] text-gray-500 block mb-1">Standard name (editable)</label>
                    <input type="text" value={sel.canonicalName}
                      onChange={e => setSelections(prev => ({ ...prev, [groupIdx]: { ...prev[groupIdx], canonicalName: e.target.value } }))}
                      className="w-full px-2.5 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex items-center justify-between flex-shrink-0 bg-gray-50">
          <span className="text-xs text-gray-500">{pendingCount} group{pendingCount !== 1 ? 's' : ''} ready to merge</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3.5 py-1.5 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 text-gray-700">Close</button>
            {pendingCount > 0 && (
              <button onClick={handleMergeAll} disabled={merging !== null}
                className="px-3.5 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                Merge All ({pendingCount})
              </button>
            )}
          </div>
        </div>
      </div>
  );
};

export default DedupFirmsModal;
